import { useQuery, type UseQueryOptions, type UseQueryResult } from '@tanstack/react-query';

import { useAuth } from '../features/auth/auth-context';
import { isSessionPrivateQueryKey, sessionQueryKey } from '../lib/session-query-cache';

/** Isolate caller-owned results while leaving public/catalogue query keys unchanged. */
export function useSessionQuery<TQueryFnData, TError = Error, TData = TQueryFnData>(
  options: Omit<UseQueryOptions<TQueryFnData, TError, TData>, 'enabled'> & { enabled?: boolean },
): UseQueryResult<TData, TError> {
  const { status, user } = useAuth();
  const privateQuery = isSessionPrivateQueryKey(options.queryKey);
  return useQuery({
    ...options,
    queryKey: sessionQueryKey(options.queryKey, user?.id ?? null),
    enabled: privateQuery
      ? status === 'authenticated' && user !== null && options.enabled !== false
      : options.enabled,
    // Never carry the previous observer's private result across an identity/key change.
    ...(privateQuery ? { placeholderData: undefined } : {}),
  });
}
